import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  registerTotpContract,
  verifyTotpContract,
  removeTotpContract,
  type RegisterTotpResponse,
  type VerifyTotpRequest,
  type VerifyTotpResponse,
  type RemoveTotpResponse
} from '@bliks-io/contracts';
import { toast } from 'sonner';
import { apiClient } from '../client';
import { userKeys } from '../query-keys';
import {
  handleSensitiveMutationError,
  type SensitiveMutationOptions
} from '@/lib/auth/stepUpReauth';
import i18next from '@/lib/i18n';

/**
 * Start a TOTP registration. Returns the otpauth:// URI (rendered as a QR
 * code) and the manual-entry secret. No success toast — the returned data
 * drives the setup view directly.
 */
export function useRegisterTotp({ onReauthRequired }: SensitiveMutationOptions) {
  return useMutation({
    mutationFn: async (): Promise<RegisterTotpResponse> => {
      const { data } = await apiClient.post<RegisterTotpResponse>(registerTotpContract.path, {});
      return data;
    },
    onError: (error: unknown) =>
      handleSensitiveMutationError(error, 'userSettings.security.twoFactorSetupError', () =>
        onReauthRequired({ kind: 'totp-register' })
      )
  });
}

/** Confirm the pending TOTP registration with a 6-digit code. */
export function useVerifyTotp({ onReauthRequired }: SensitiveMutationOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: VerifyTotpRequest): Promise<VerifyTotpResponse> => {
      const { data } = await apiClient.post<VerifyTotpResponse>(verifyTotpContract.path, request);
      return data;
    },
    onSuccess: () => {
      toast.success(i18next.t('userSettings.security.twoFactorSetupSuccess'));
      return queryClient.invalidateQueries({ queryKey: userKeys.security() });
    },
    // The reload discards the pending QR/secret — the honest resume is
    // restarting setup, not retrying the verify.
    onError: (error: unknown) =>
      handleSensitiveMutationError(error, 'userSettings.security.twoFactorSetupError', () =>
        onReauthRequired({ kind: 'totp-register' })
      )
  });
}

/** Remove the configured TOTP factor. */
export function useRemoveTotp({ onReauthRequired }: SensitiveMutationOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<RemoveTotpResponse> => {
      const { data } = await apiClient.delete<RemoveTotpResponse>(removeTotpContract.path);
      return data;
    },
    onSuccess: () => {
      toast.success(i18next.t('userSettings.security.twoFactorRemoveSuccess'));
      return queryClient.invalidateQueries({ queryKey: userKeys.security() });
    },
    onError: (error: unknown) =>
      handleSensitiveMutationError(error, 'userSettings.security.twoFactorRemoveError', () =>
        onReauthRequired({ kind: 'totp-remove' })
      )
  });
}
